import { useQueryClient } from "@tanstack/react-query";
import { usePutFarmGateProcessData } from "../../../../../api/cultivations";
import { usePutProcessGateProcessData } from "../../../../../api/process";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { buildProcessUpdateDto } from "../utils/processRecords.utils";
import { ApiProcessingRecord, GateKind, ProcessSaveContext } from "../types/processRecords.types";

interface UseProcessRecordCrudParams {
  processes: ApiProcessingRecord[];
  gateKind: GateKind;
  processGateId: string;
  ownerId: string;
  sourceId: string;
  /** Propagates a successfully saved record back into the page's gate state. */
  onUpdate: (next: ApiProcessingRecord[]) => void;
  /** Leaves inline-edit mode once the process PUT succeeds. */
  onSaved: () => void;
}

const detailQueryKey = (gateKind: GateKind) =>
  gateKind === "farm" ? "getFarmGateProcessDataById" : "getProcessGateProcessDataById";

/**
 * Persistence for the process rows. Both flows ride the same
 * `PUT /{gate}/processing/{processId}` endpoint and differ only in whether the
 * authoritative `cipSip` array is included — see {@link buildProcessUpdateDto}.
 */
export const useProcessRecordCrud = ({
  processes,
  gateKind,
  processGateId,
  ownerId,
  sourceId,
  onUpdate,
  onSaved
}: UseProcessRecordCrudParams) => {
  const queryClient = useQueryClient();

  const { mutate: putFarmGateProcessData } = usePutFarmGateProcessData();
  const { mutate: putProcessGateProcessData } = usePutProcessGateProcessData();

  const ctx: ProcessSaveContext = { gateKind, processGateId, ownerId, sourceId };

  const saveProcess = (id: string, updated: ApiProcessingRecord) => {
    const payload = buildProcessUpdateDto(updated, ctx, { includeCipSip: false });
    const opts = {
      onSuccess: () => {
        onSaved();
        onUpdate(processes.map(x => (x._id === id ? updated : x)));
        successMessage("Process updated");
      },
      onError: (error: any) => errorView(error?.msg || "Failed to update process")
    };

    if (gateKind === "farm") {
      putFarmGateProcessData(payload, opts);
    } else {
      putProcessGateProcessData(payload, opts);
    }
  };

  /**
   * `updated` here is sourced from the loaded ProcessDetail (see ProcessDetailPanel),
   * so its `cipSip` array is authoritative and rides the PUT.
   */
  const saveCipSip = (id: string, updated: ApiProcessingRecord) => {
    const payload = buildProcessUpdateDto(updated, ctx, { includeCipSip: true });
    const opts = {
      onSuccess: () => {
        successMessage("CIP/SIP saved");
        queryClient.invalidateQueries([detailQueryKey(gateKind), id]);
      },
      onError: (error: any) => errorView(error?.msg || "Failed to save CIP/SIP")
    };

    if (gateKind === "farm") {
      putFarmGateProcessData(payload, opts);
    } else {
      putProcessGateProcessData(payload, opts);
    }
  };

  return { saveProcess, saveCipSip };
};
